import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../context/Context";

const Answer = () => {
  const { questionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [userData] = useContext(UserContext);

  const [answers, setAnswers] = useState([]);
  const [newAnswer, setNewAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

  const selectedQuestion = location.state?.question;

  const fetchAnswers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/Answer/${questionId}`,
        {
          headers: {
            "x-auth-token": localStorage.getItem("auth-token") || "",
          },
        },
      );

      setAnswers(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch answers:", error);
      setErrorMessage("Unable to load answers right now. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, questionId]);

  useEffect(() => {
    if (!userData?.user) {
      navigate("/login");
      return;
    }

    fetchAnswers();
  }, [userData, navigate, fetchAnswers]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newAnswer.trim()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await axios.post(
        `${apiBaseUrl}/api/Answer/`,
        {
          answer: newAnswer,
          questionId,
          user_id: userData.user?.id,
        },
        {
          headers: {
            "x-auth-token": localStorage.getItem("auth-token") || "",
          },
        },
      );

      setNewAnswer("");
      await fetchAnswers();
    } catch (error) {
      console.error("Failed to submit answer:", error);
      setErrorMessage("Unable to submit your answer. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-100 w-full py-8 px-4 md:px-16">
      <div className="max-w-4xl mx-auto bg-white rounded-md shadow-sm p-6 md:p-8">
        <div className="mb-6 border-b pb-4">
          <Link to="/" className="text-blue-600 hover:underline text-sm">
            Back to questions
          </Link>
          <h2 className="text-2xl font-semibold mt-2">Question</h2>
          <p className="text-gray-700 mt-2">
            {selectedQuestion?.question ||
              "Question details are not available."}
          </p>
        </div>

        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-3">Answers</h3>

          {isLoading ? (
            <p className="text-gray-500">Loading answers...</p>
          ) : errorMessage ? (
            <p className="text-red-600">{errorMessage}</p>
          ) : answers.length === 0 ? (
            <p className="text-gray-500">
              No answers yet. Be the first to answer.
            </p>
          ) : (
            <div className="space-y-3">
              {answers.map((item) => (
                <div
                  key={item.answer_id}
                  className="border rounded p-4 bg-gray-50"
                >
                  <p className="text-gray-800">{item.answer_text}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    By: {item.user_name}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <h3 className="text-xl font-semibold mb-3">Your Answer</h3>
          <textarea
            value={newAnswer}
            onChange={(e) => setNewAnswer(e.target.value)}
            className="w-full border rounded p-3 min-h-36 focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Type your answer here..."
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-3 bg-blue-600 text-white px-6 py-2 rounded hover:bg-orange-500 disabled:opacity-50"
          >
            {isSubmitting ? "Submitting..." : "Post Your Answer"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Answer;
